/**
 * AI-credit gate — blocks AI features when the user has NO commercial credit
 * AND OSS is unavailable (no GPU / disabled by admin), prompting them to buy
 * credits. Also exposes a toast helper for mid-task credit exhaustion.
 *
 *   const gate = useAiCreditGate()           // { blocked, purchaseRequired, ... }
 *   if (gate.blocked) return <AiCreditGate /> // or render <AiCreditGate banner/>
 *   notifyCreditExhausted(navigate, msg)      // mid-task pause/stop toast
 */
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ShoppingCart, Leaf } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useProviderMode, useSetProviderMode } from '@/services/llm-mode.service'

const BUY_ROUTE = '/self-manage/finances'

export function useAiCreditGate(estimated = 5) {
  const { data: status, isLoading, refetch } = useProviderMode(estimated)
  return {
    status,
    isLoading,
    refetch,
    blocked: !!status?.action_blocked,
    purchaseRequired: !!status?.purchase_required,
    ossAvailable: status?.oss_available !== false,
    message: status?.block_message ||
      'Vos crédits IA sont épuisés. Achetez des crédits pour utiliser cette fonctionnalité.',
  }
}

/** Imperative pre-flight: returns true if the AI action is allowed to start. */
export function useEnsureAiCredits(estimated = 5) {
  const { refetch } = useProviderMode(estimated)
  const navigate = useNavigate()
  return async (): Promise<boolean> => {
    try {
      const { data } = await refetch()
      if (data?.action_blocked) {
        notifyCreditExhausted(navigate, data.block_message)
        return false
      }
    } catch { /* fail-open */ }
    return true
  }
}

/** Mid-task / pre-flight toast with a Buy CTA. */
export function notifyCreditExhausted(navigate: (to: string) => void, message?: string | null) {
  toast.error(message || 'Crédits IA épuisés.', {
    duration: 10000,
    action: { label: 'Acheter des crédits', onClick: () => navigate(BUY_ROUTE) },
  })
}

interface AiCreditGateProps {
  estimated?: number
  /** banner = inline strip; card = prominent block. Default card. */
  variant?: 'banner' | 'card'
  className?: string
}

export default function AiCreditGate({ estimated = 5, variant = 'card', className = '' }: AiCreditGateProps) {
  const gate = useAiCreditGate(estimated)
  const navigate = useNavigate()
  const setMode = useSetProviderMode()

  if (!gate.blocked) return null

  const buy = (
    <Button size="sm" onClick={() => navigate(BUY_ROUTE)} data-testid="ai-buy-credits">
      <ShoppingCart className="h-4 w-4 mr-1.5" />Acheter des crédits IA
    </Button>
  )
  // OSS is unavailable in the blocked state (that's why it's blocked), so we only
  // offer the buy path. (If OSS later becomes available, the gate auto-clears.)

  if (variant === 'banner') {
    return (
      <div className={`flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200 ${className}`}
        data-testid="ai-credit-gate">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="flex-1">{gate.message}</span>
        {buy}
      </div>
    )
  }

  return (
    <div className={`rounded-lg border border-amber-300 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-950/40 ${className}`}
      data-testid="ai-credit-gate">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2 dark:bg-amber-900"><AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-300" /></div>
        <div className="flex-1 space-y-2">
          <p className="font-semibold text-amber-900 dark:text-amber-100">Crédits IA requis</p>
          <p className="text-sm text-amber-800 dark:text-amber-200">{gate.message}</p>
          <div className="flex items-center gap-2 pt-1">{buy}</div>
        </div>
      </div>
    </div>
  )
}
